/**
 * @module components/properties/PropertiesPanel
 * @summary Right-hand inspector for the selected shape or connection.
 * @description
 *   Inputs are UNCONTROLLED (defaultValue) and the whole form is keyed by the
 *   selected id, so: (a) typing mutates the engine model live via applyProp
 *   without a React round-trip, and (b) selecting a different item remounts the
 *   form with fresh values. This mirrors the original's updateProps/bind without
 *   the controlled-input fighting.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 503-547 + updateProps/bind (3900-3979).
 */
import { engine } from '../../engine/CanvasEngine';
import { useUiStore } from '../../state/uiStore';
import { FeedEditor } from '../formulation/FeedEditor';
import { FG_START_TYPES, isFgStart } from '../../engine/formulation';
import '../../styles/formulation.css';

/**
 * Symbols that are drawing furniture rather than plant. Nothing flows through a
 * title block, so it is not offered a throughput.
 */
const ANNOTATION = new Set(['zonebox', 'textlabel', 'processarrow', 'titleblock']);

export function PropertiesPanel() {
  const selection = useUiStore((s) => s.selection);
  const selVersion = useUiStore((s) => s.selVersion);
  const shape = selection.shape;
  const conn = selection.connection;
  const isConn = !!conn && !shape;
  const target = shape ?? conn;
  // selVersion remounts the uncontrolled form after canvas-side edits (drag,
  // resize, nudge, align, undo) so X/Y/W/H show the CURRENT geometry.
  const key = `${target?.id ?? 'none'}:${selVersion}`;

  if (selection.kind === 'none' || !target) {
    return (
      <div id="props">
        <div id="props-hdr">Properties</div>
        <div id="props-scroll">
          <div id="props-empty">Select a shape or connection to edit its properties.</div>
        </div>
      </div>
    );
  }

  const ap = (field: string) => (e: { target: { value: string } }) =>
    engine.applyProp(field, e.target.value);
  const rot = shape?.rotation || 0;

  return (
    <div id="props">
      <div id="props-hdr">Properties{selection.kind === 'multi' ? ` (${selection.count})` : ''}</div>
      <div id="props-scroll" key={key}>
        {/* Identity */}
        <div className="ps">Identity</div>
        <div className="pr">
          <div className="pl">Label</div>
          <input className="pi" defaultValue={target.label || ''} onChange={ap('label')} />
        </div>
        {!isConn && (
          <div className="pr">
            <div className="pl">Tag / P&amp;ID No.</div>
            <input className="pi" defaultValue={shape!.tag || ''} onChange={ap('tag')} placeholder="E-101" />
          </div>
        )}
        <div className="pr">
          <div className="pl">Notes</div>
          <textarea className="pta" defaultValue={shape?.notes || ''} onChange={ap('notes')} />
        </div>

        {/* Process. One number per source is all a mass balance needs; the rest
            of the sheet is worked out from it and written on the lines. */}
        {(isConn || !ANNOTATION.has(shape!.type)) && (
          <>
            <div className="ps">Process</div>
            <div className="pr">
              <div className="pl">{isConn ? 'Throughput' : 'Feed'}</div>
              <input
                className="pi"
                type="number"
                min={0}
                step={10}
                placeholder="kg/h"
                title={
                  isConn
                    ? 'Pin this stream to a throughput. Leave it empty and the balance divides what arrives evenly between the streams leaving that machine — pin the odd one out, such as the fines off a cyclone, and the rest share what is left.'
                    : 'Material this item FEEDS INTO the line, in kg/h. What arrives from upstream is carried through on top of it, so a machine fed from above normally leaves this empty.'
                }
                defaultValue={target.rate ?? ''}
                onChange={ap('rate')}
              />
            </div>
          </>
        )}

        {/* Formulation. Which line this item is on and what it charges in —
            the other half of the throughput. Offered on any equipment for the
            same reason the Feed box is: a drawing knows which of its symbols
            is a feeder far better than a hard-coded list of type keys does. */}
        {!isConn && !ANNOTATION.has(shape!.type) && (
          <>
            <div className="ps">Materials charged here</div>
            <FeedEditor shape={shape!} version={selVersion} />

            <div className="ps">Finished good</div>
            <div className="pr">
              <div className="pl">Starts here</div>
              <select
                className="pse"
                defaultValue={shape!.fgStart === undefined ? 'auto' : shape!.fgStart ? 'on' : 'off'}
                title="Where the recipe ends and the product begins. The compound is made in the extruder or kneader, so its outlet does this on its own; pin it when the line is built differently."
                onChange={(e) => engine.setFgStart(e.target.value as 'auto' | 'on' | 'off')}
              >
                <option value="auto">
                  Auto{FG_START_TYPES.has(shape!.type) ? ' — yes, it compounds' : ' — no'}
                </option>
                <option value="on">Yes, the FG starts here</option>
                <option value="off">No, it carries on as a mixture</option>
              </select>
            </div>
            {isFgStart(shape!) && (
              <div className="fe-note">
                The finished good starts here and is carried to the packing area. Its code is the
                one on this item&rsquo;s line, in the Formulation tab.
              </div>
            )}
          </>
        )}

        {/* Appearance */}
        <div className="ps">Appearance</div>
        {!isConn && (
          <div className="pr">
            <div className="pl">Fill Color</div>
            <div className="pcr">
              <input className="pc" type="color" defaultValue={shape!.fillColor || '#ffffff'} onChange={ap('fillColor')} />
              <input className="pi" defaultValue={shape!.fillColor || '#ffffff'} onChange={ap('fillColor')} />
            </div>
          </div>
        )}
        <div className="pr">
          <div className="pl">{isConn ? 'Line Color' : 'Stroke Color'}</div>
          <div className="pcr">
            <input
              className="pc"
              type="color"
              defaultValue={(isConn ? conn!.color : shape!.strokeColor) || '#1a1a1a'}
              onChange={ap('stroke')}
            />
            <input
              className="pi"
              defaultValue={(isConn ? conn!.color : shape!.strokeColor) || '#1a1a1a'}
              onChange={ap('stroke')}
            />
          </div>
        </div>
        <div className="pr">
          <div className="pl">{isConn ? 'Line Width' : 'Stroke Width'}</div>
          <input
            className="pi"
            type="number"
            min={0.5}
            max={8}
            step={0.5}
            defaultValue={(isConn ? conn!.lineWidth : shape!.strokeWidth) ?? 1.5}
            onChange={ap('strokeWidth')}
          />
        </div>
        {!isConn && (
          <div className="pr">
            <div className="pl">Font Size</div>
            <input className="pi" type="number" min={6} max={24} defaultValue={shape!.fontSize ?? 10} onChange={ap('fontSize')} />
          </div>
        )}
        {!isConn && (
          <div className="pr">
            <div className="pl">Label Position</div>
            <select className="pse" defaultValue={shape!.labelPos || 'bottom'} onChange={ap('labelPos')}>
              <option value="bottom">Bottom</option>
              <option value="top">Top</option>
              <option value="center">Center</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </div>
        )}

        {/* Connection line properties */}
        {isConn && (
          <>
            <div className="ps">Line</div>
            <div className="pr">
              <div className="pl">Style</div>
              <select className="pse" defaultValue={conn!.lineStyle || 'solid'} onChange={ap('lineStyle')}>
                <option value="solid">Solid</option>
                <option value="dashed">Dashed (long)</option>
                <option value="dashed-short">Dashed (short)</option>
                <option value="dotted">Dotted</option>
                <option value="dash-dot">Dash-dot</option>
              </select>
            </div>
            <div className="pr">
              <div className="pl">Arrow</div>
              <select className="pse" defaultValue={conn!.arrowHead || 'forward'} onChange={ap('arrowHead')}>
                <option value="forward">Forward ▶</option>
                <option value="none">None</option>
                <option value="both">◀▶ Both</option>
              </select>
            </div>
            <div className="pr">
              <div className="pl">Routing</div>
              <select className="pse" defaultValue={conn!.routeMode || ''} onChange={ap('routeMode')}>
                <option value="">Default</option>
                <option value="manhattan">Orthogonal</option>
                <option value="curved">Curved</option>
              </select>
            </div>
            <button className="pb" onClick={() => engine.resetRoute()}>↺ Reset routing</button>
          </>
        )}

        {/* Geometry */}
        {!isConn && (
          <>
            <div className="ps">Geometry</div>
            <div className="pr p2c">
              <input className="pi" type="number" defaultValue={Math.round(shape!.x)} onChange={ap('x')} title="X" />
              <input className="pi" type="number" defaultValue={Math.round(shape!.y)} onChange={ap('y')} title="Y" />
            </div>
            <div className="pr p2c">
              <input className="pi" type="number" min={20} defaultValue={Math.round(shape!.w)} onChange={ap('w')} title="Width" />
              <input className="pi" type="number" min={20} defaultValue={Math.round(shape!.h)} onChange={ap('h')} title="Height" />
            </div>

            <div className="ps">Rotation</div>
            <div className="rotate-row">
              {[0, 90, 180, 270].map((deg) => (
                <button
                  key={deg}
                  className={'rotate-btn' + (rot === deg ? ' active' : '')}
                  onClick={() => engine.setRotation(deg)}
                >
                  {deg}°
                </button>
              ))}
            </div>

            <div className="ps">Z-Order</div>
            <div className="pbr">
              <button className="pb" onClick={() => engine.bringToFront()}>▲ Bring Front</button>
              <button className="pb" onClick={() => engine.sendToBack()}>▼ Send Back</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
